/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import { BackstageOverrides } from '@backstage/core-components';
import { BackstageOverrides as CatalogReactOverrides } from '@backstage/plugin-catalog-react';
import {
  createUnifiedThemeFromV4,
  pageTheme as defaultPageThemes,
  PageTheme,
  palettes,
  UnifiedTheme,
  createTheme,
} from '@backstage/theme';

import { alpha, Theme } from '@material-ui/core/styles';
import { AlertClassKey } from '@material-ui/lab';
import { AutocompleteClassKey } from '@material-ui/lab/Autocomplete';
import { ToggleButtonClassKey } from '@material-ui/lab/ToggleButton';

type FillColorPalette = {
  primary: string;
  secondary: string;
  tertiary: string;
  brand: string;
  menu?: {
    selected: string;
    highlighted: string;
  };
  headerNav: {
    hover: string;
  };
};

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    fill: FillColorPalette;
  }
  interface PaletteOptions {
    fill: FillColorPalette;
  }
}

declare module '@material-ui/core/styles/overrides' {
  export interface ComponentNameToClassKey {
    MuiAlert: AlertClassKey;
    MuiAutocomplete: AutocompleteClassKey;
    MuiToggleButton: ToggleButtonClassKey;
  }
}

const entityTypeTheme: Record<string, { colors: string[]; fontColor: string }> =
  {
    service: {
      colors: ['#147D64'],
      fontColor: '#FFFFFF',
    },
    website: {
      colors: ['#044E54'],
      fontColor: '#FFFFFF',
    },
    library: {
      colors: ['#415853'],
      fontColor: '#FFFFFF',
    },
  };

const pageThemesFontColorOverride: Record<string, PageTheme> = {
  openapi: {
    colors: ['#506C65'],
    fontColor: '#FFFFFF',
    shape: '',
    backgroundImage: '',
  },
};

Object.keys(defaultPageThemes).forEach(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    fontColor: '#121212',
  };
  if (entityTypeTheme[key]) {
    pageThemesFontColorOverride[key].colors = entityTypeTheme[key].colors;
  }
});

// TODO: Check how to replace deprecated createTheme with createUnifiedTheme
const baseTheme = createTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: '#147D64',
      light: '#02A783',
      dark: '#0C6B58',
    },
    secondary: {
      main: '#341E86',
    },
    navigation: {
      ...palettes.light.navigation,
      background: '#FFFFFF',
      color: '#595959',
      indicator: '#FFFFFF',
      navItem: {
        hoverBackground: '#FFFFFF',
      },
    },
    text: {
      primary: '#121212',
      secondary: '#595959',
    },
    background: {
      default: '#FFFFFF',
    },
    fill: {
      primary: '#F8FAF9',
      secondary: '#FFFFFF',
      tertiary: '#EBEFEE',
      brand: '#05322B',
      menu: {
        selected: '#C6F7E2',
        highlighted: 'EBEFEE',
      },
      headerNav: {
        hover: '#014D40',
      },
    },
    border: '#CED7D4',
  },
  fontFamily: 'Poppins, sans-serif',
  pageTheme: pageThemesFontColorOverride,
  defaultPageTheme: 'home',
});

const createCustomThemeOverrides = (
  theme: Theme,
): BackstageOverrides & CatalogReactOverrides => {
  return {
    MuiCssBaseline: {
      '@global': {
        html: {
          height: '100%',
        },
        body: {
          overflowY: 'hidden',
        },
        a: {
          color: 'inherit',
          textDecoration: 'none',
        },
        'strong, b': {
          fontWeight: 600,
        },
      },
    },
    BackstageDismissableBanner: {
      topPosition: {
        top: '88px',
        zIndex: 10000,
      },
    },
    BackstageHeader: {
      header: {
        backgroundImage: 'unset',
        boxShadow: 'unset',
        paddingBottom: theme.spacing(1),
      },
      title: {
        color: theme.page.fontColor,
        fontWeight: 600,
        fontSize: '24px',
      },
      subtitle: {
        color: alpha(theme.page.fontColor, 0.8),
      },
      type: {
        color: alpha(theme.page.fontColor, 0.8),
      },
    },
    // TODO: Remove after https://github.com/backstage/backstage/pull/16853 is available here
    BackstageHeaderLabel: {
      label: {
        color: theme.page.fontColor,
      },
      value: {
        color: alpha(theme.page.fontColor, 0.8),
      },
    },
    BackstageHeaderTabs: {
      tabsWrapper: {
        backgroundColor: theme.palette.fill.primary,
      },
      defaultTab: {
        fontSize: 'inherit',
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(2),
      },
    },
    BackstageInfoCard: {
      headerTitle: {
        fontWeight: 600,
      },
    },
    BackstageOpenedDropdown: {
      icon: {
        '& path': {
          fill: theme.palette.fill.secondary,
        },
      },
    },
    BackstagePage: {
      root: {
        height: 'calc(100vh - 64px)',
        marginTop: '64px',
        display: 'block',
        backgroundColor: theme.palette.fill.primary,
      },
    },
    BackstageSidebar: {
      drawer: {
        top: 64,
        borderRight: `1px solid ${theme.palette.border}`,
      },
    },
    BackstageSidebarItem: {
      root: {
        '&:hover': {
          color: theme.palette.text.primary,
          '& $iconContainer': {
            background: theme.palette.fill.tertiary,
          },
        },
      },
      label: {
        fontWeight: 500,
        marginLeft: theme.spacing(1),
      },
      closedItemIcon: {
        background: 'transparent',
        width: 'auto',
        paddingRight: '8px',
        padding: '8px',
        borderRadius: '50%',
      },
      iconContainer: {
        width: 'auto',
        height: 'auto',
        marginLeft: theme.spacing(2) + 2,
        padding: '8px',
        marginRight: 0,
        background: 'transparent',
        borderRadius: '50%',
      },
      selected: {
        '&$root': {
          color: theme.palette.text.primary,
        },
        '& $closedItemIcon': {
          background: `${theme.palette.fill.menu?.selected} !important`,
          paddingRight: '8px',
        },
        '& $iconContainer': {
          background: `${theme.palette.fill.menu?.selected} !important`,
          marginLeft: theme.spacing(2) + 1,
        },
      },
    },
    BackstageSupportButton: {
      popoverList: {
        '&> li': {
          whiteSpace: 'normal',
        },
      },
    },
    BackstageTable: {
      root: {
        '&> :first-child': {
          border: `1px solid ${theme.palette.border}`,
          boxShadow: 'none',
        },
        '& th': {
          borderTop: 'none',
          textTransform: 'none !important',
          background: theme.palette.fill.tertiary,
          height: '50px',
          color: theme.palette.text.primary,
          fontWeight: 500,
        },
        '& td': {
          borderBottom: `1px solid ${theme.palette.border}`,
        },
      },
    },
    BackstageTableToolbar: {
      root: {
        padding: theme.spacing(2),
        minHeight: theme.spacing(6),
      },
    },
    CatalogReactUserListPicker: {
      title: {
        textTransform: 'none',
      },
    },
    MuiAlert: {
      root: {
        borderRadius: 0,
      },
      standardError: {
        color: '#FFFFFF',
        backgroundColor: theme.palette.error.dark,
        '& $icon': {
          color: '#FFFFFF',
        },
      },
      standardInfo: {
        color: '#FFFFFF',
        backgroundColor: theme.palette.primary.dark,
        '& $icon': {
          color: '#FFFFFF',
        },
      },
      standardSuccess: {
        color: '#FFFFFF',
        backgroundColor: theme.palette.success.dark,
        '& $icon': {
          color: '#FFFFFF',
        },
      },
      standardWarning: {
        color: theme.palette.grey[700],
        backgroundColor: theme.palette.secondary.light,
        '& $icon': {
          color: theme.palette.grey[700],
        },
      },
    },
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: `${theme.palette.fill.brand}`,
      },
    },
    MuiAutocomplete: {
      root: {
        '&[aria-expanded=true]': {
          color: '#FFFFFF',
        },
      },
    },
    MuiBackdrop: {
      root: {
        backgroundColor: 'rgba(9,30,69,0.54)',
      },
    },
    MuiBadge: {
      anchorOriginTopRightCircle: {
        top: '6%',
        right: '6%',
      },
    },
    MuiButton: {
      root: {
        borderRadius: 3,
        textTransform: 'none',
      },
      contained: {
        boxShadow: 'none',
      },
    },
    MuiCard: {
      root: {
        border: `1px solid ${theme.palette.border}`,
        boxShadow: 'none',
      },
    },
    MuiChip: {
      root: {
        borderRadius: 3,
        backgroundColor: theme.palette.grey[50],
        color: theme.palette.primary.dark,
        margin: 4,
      },
    },
    MuiLink: {
      root: {
        color: '#02A783',
        '&.qetaAskQuestionBtn': {
          color: '#FFFFFF',
        },
      },
    },
    MuiSelect: {
      root: {
        '&[aria-expanded]': {
          backgroundColor: '#0C6B58',
          color: '#FFFFFF',
        },
      },
    },
    MuiSwitch: {
      root: {
        padding: 10,
      },
      switchBase: {
        padding: 12,
      },
      thumb: {
        backgroundColor: '#FFFFFF',
        height: 14,
        width: 14,
      },
      track: {
        borderRadius: 9,
      },
    },
    MuiTabs: {
      indicator: {
        transition: 'none',
      },
    },
    MuiToggleButton: {
      sizeSmall: {
        padding: theme.spacing(0.7),
      },
    },
    MuiTypography: {
      h1: {
        fontSize: '40px',
        fontWeight: 600,
      },
      h2: {
        fontSize: '32px',
        fontWeight: 600,
      },
      h3: {
        fontSize: '24px',
        fontWeight: 600,
      },
      h4: {
        fontSize: '20px',
        fontWeight: 600,
      },
      h5: {
        fontSize: '16px',
        fontWeight: 600,
      },
      h6: {
        fontSize: '14px',
        fontWeight: 600,
      },
      body1: {
        fontSize: '16px',
      },
      body2: {
        fontSize: '14px',
      },
      button: {
        textTransform: 'none',
      },
    },
  };
};

export const bhTheme: UnifiedTheme = createUnifiedThemeFromV4({
  ...baseTheme,
  overrides: {
    ...baseTheme.overrides,
    ...createCustomThemeOverrides(baseTheme),
  },
});
